import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { ColorPallet } from "../utils/colorPallet";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import AllTable from "../components/commontable/table";
import { getBatchData } from "../features/batch/batchSlice";
import WarningIcon from "../components/IconComp/warningIcon";
import WarningIconYellow from "../components/IconComp/warningIconYellow";
import { useLocation } from "react-router-dom";
import { EditBatchToolbar } from "../components/inventory/batch/batch-edit-toolbar";
import LoadingSpinner from "../components/common/Loading";
import SearchIcon from "../components/IconComp/searchIcon";
import { BatchHeader } from "../utils/constants";
import usePermissions from "../hooks/usePermissions";
import { getAllStaffPermissions } from "../features/staff/staffSlice";
import DeleteIcon from "../components/IconComp/deleteIcon";
import HideIcon from "../components/IconComp/hideIcon";
import { HideBatchModel } from "../components/inventory/batch/batch-hide-model";
import AppSnackbar from "../components/common/AppSnackbar";
import { get_shelf_system } from "../features/shelfNumberSystem/shelfNumberService";
interface IProps {
  getMedicine: any;
}
const Batch = ({ getMedicine }: IProps) => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const isFromExpireMedBatch =
    location.state && location.state.fromExpireMedBatch;

  const [showExpired, setShowExpired] = useState(false);
  const [showNearExpiry, setShowNearExpiry] = useState(false);
  const [batchData, setBatchData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [expireMedCount, setExpireMedCount] = useState<any>();
  const [nearExpireMedCount, setNearExpireMedCount] = useState<any>();
  //pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(""); // Debounce
  const rowData = useAppSelector((state) => state?.batch);
  const [selectedDate, setSelectedDate] = useState<string>(); // Set the current date by default
  const pharmacyId = localStorage?.getItem("pharmacyId");
  const isPermissions = usePermissions();
  const Account_Type = parseInt(
    localStorage.getItem("pharmacy_account_type") || "0",
    10
  );
  const [selectedId, setSelectedId] = useState<any>(undefined);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const { staffPermissionsPayload }: any = useAppSelector(
    (state) => state?.staff
  );
  const pharmacy_Id: any = localStorage?.getItem("pharmacyId");
  const [addedByChecked, setAddedByChecked] = useState(false);
  const [updatedByChecked, setUpdatedByChecked] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [apiError, setApiError] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const handleOpenModal = (id: number) => {
    setSelectedBatchId(id);
    setOpenModal(true);
  };
  const handleCloseModal = () => {
    setApiError(false);
    setOpenModal(false);
  };

  // Debounce effect for search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // Adjust debounce delay as needed
    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  const handleChange = (event: any) => {
    setSelectedId(event?.target?.value);
    setPage(0);
  };

  const handleAddedByChange = (event: any) => {
    setAddedByChecked(event.target.checked);
    setPage(0);
  };

  const handleUpdatedByChange = (event: any) => {
    setUpdatedByChecked(event.target.checked);
    setPage(0);
  };

  const GetAllStaffPermissions = useCallback(() => {
    dispatch(getAllStaffPermissions(pharmacy_Id)).then((res: any) => {
      if (res?.payload?.success === true) {
      }
    });
  }, [dispatch]);

  useEffect(() => {
    if (pharmacy_Id) {
      GetAllStaffPermissions();
    }
  }, [pharmacy_Id, GetAllStaffPermissions]);

  const options = staffPermissionsPayload
    ? staffPermissionsPayload?.map((item: any) => ({
        id: item?.staff?.id,
        name:
          item?.staff?.name.charAt(0).toUpperCase() +
          item?.staff?.name.slice(1).toLowerCase(),
      }))
    : [];

  const getBatch = useCallback(async () => {
    const dto: any = {
      page: page + 1, // API pages are 1-indexed
      limit: rowsPerPage,
      search: debouncedSearchQuery,
      date: selectedDate, // Assuming there's a selected date
      id: selectedId ? selectedId : undefined, // Assuming there's an ID filter
      added_by: addedByChecked ? addedByChecked : undefined,
      updated_by: updatedByChecked ? updatedByChecked : undefined,
      expire_med: showExpired ? showExpired : undefined,
      near_expire: showNearExpiry ? showNearExpiry : undefined,
    };
    try {
      setIsLoading(true);
      const res = await dispatch(
        getBatchData({ pharmacy_id: pharmacyId, dto })
      );
      if (res.payload) {
        setTotalCount(res?.payload?.pagination?.totalItems || 0); // Ensure number type
        setExpireMedCount(res?.payload?.counts?.expireCount || 0);
        setNearExpireMedCount(res?.payload?.counts?.nearExpireCount || 0);
      }
    } catch (error) {
      console.error("Error fetching manufacturer data:", error);
    } finally {
      setIsLoading(false);
      // setTimeout(() => {
      //   setIsLoading(false);
      // }, 500);

      // Only focus if there's an active search query
      if (debouncedSearchQuery) {
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 0);
      }
    }
  }, [
    dispatch,
    pharmacyId,
    page,
    rowsPerPage,
    debouncedSearchQuery,
    selectedDate,
    selectedId,
    showExpired,
    showNearExpiry,
    addedByChecked,
    updatedByChecked,
  ]);

  // Preserve cursor position while typing
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { selectionStart, selectionEnd } = e.target; // Save cursor position
    setSearchQuery(e.target.value);
    setPage(0);
    // Restore cursor position after state update
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.setSelectionRange(selectionStart, selectionEnd);
      }
    }, 0);
  };

  const hasAccess =
    Account_Type === 0 || (isPermissions?.view && Account_Type === 1);

  useEffect(() => {
    if (hasAccess) {
      getBatch();
      if (isFromExpireMedBatch) {
        setShowExpired(true);
      }
    } else {
      setIsLoading(false);
    }
  }, [getBatch, isPermissions.view, Account_Type, isFromExpireMedBatch]);

  const tableRow = rowData?.batchDetails?.map((item: any) => {
    const currentDate = new Date();
    const expiryDate = new Date(item?.expiry_date);

    // Convert expiry_reminder months into days
    const reminderMonths = item?.expiry_reminder
      ? parseInt(item.expiry_reminder, 10)
      : 6;
    const reminderDays = reminderMonths * 30; // Approximate each month as 30 days

    const daysUntilExpiry = Math?.floor(
      (expiryDate?.getTime() - currentDate?.getTime()) / (1000 * 60 * 60 * 24)
    );
    let expireIcon = null;

    if (daysUntilExpiry < 0) {
      // Medicine is expired
      expireIcon = <WarningIcon />;
    } else if (daysUntilExpiry <= reminderDays) {
      // Medicine is near expiry
      expireIcon = <WarningIconYellow />;
    }

    const formattedTotalCost = (
      item?.cost_price *
      (item?.quantity - item?.quantity_sold)
    ).toFixed(2);

    return {
      "Batch No.": item?.batch_no,
      "Shelf No.": item?.shelf?.name ? item?.shelf?.name : "-",

      Name: (
        <>
          {item?.pharmacy_medicine?.name}{" "}
          <span
            style={{
              fontSize: "10px",
              position: "relative",
            }}
          >
            {item?.pharmacy_medicine?.dose}
          </span>
        </>
      ),

      "Stock In": item?.quantity ? item.quantity.toFixed(2) : "-",
      "Stock Out": item?.quantity_sold?.toLocaleString("en-IN"),
      "Stock Remaining": (
        item?.quantity?.toFixed(2) - item?.quantity_sold?.toFixed(2)
      )?.toLocaleString("en-IN"),
      "Sell Price": item?.sell_price ? item.sell_price.toFixed(2) : "-",
      "Cost Price": item?.cost_price ? item.cost_price.toFixed(2) : "-",
      "Total Cost": formattedTotalCost,
      "Production Date": item?.production_date
        ? new Date(item?.production_date)?.toLocaleDateString()
        : "-",
      "Expire Date": (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {expireIcon}
          {item?.expiry_date
            ? new Date(item?.expiry_date)?.toLocaleDateString()
            : ""}
        </Box>
      ),
      "Supplier Name": item?.supplier?.supplier_company_name
        ? item?.supplier?.supplier_company_name
        : "-",
      "Supplier Invoice no.": item?.stock_inv_no ? item?.stock_inv_no : "-",
      // "Supplier Invoice Attachment": item?.stock_inv_file ? (
      //   <span
      //     style={{
      //       cursor: "pointer",
      //       color: "blue",
      //       textDecoration: "underline",
      //     }}
      //     onClick={() => {
      //       openInNewTab(
      //         BASE_API_URL +
      //           `/api/pharmacy/batch/stock/invoice/get/?batch_id=${item?.id}`
      //       );
      //     }}
      //   >
      //     {item?.stock_inv_file}
      //   </span>
      // ) : (
      //   "-"
      // ),
      // &attachment_id=${item?.file_path}
      Actions: (
        <Box sx={{ display: "flex", flexDirection: "row", gap: "8px" }}>
          {/* <DeleteIcon />  */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              padding: "5px 0px",
            }}
          >
            {(isPermissions?.edit && Account_Type === 1) ||
            Account_Type === 0 ? (
              <EditBatchToolbar
                batch={item}
                isEdit={true}
                setExpireMedCount={setExpireMedCount}
                setNearExpireMedCount={setNearExpireMedCount}
              />
            ) : null}
          </Box>

          {daysUntilExpiry < 0 ? (
            <Tooltip title="Hide">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  cursor: "pointer",
                  padding: "5px 0px",
                }}
              >
                <HideIcon onClick={() => handleOpenModal(item?.id)} />
              </Box>
            </Tooltip>
          ) : null}
        </Box>
      ),
    };
  });

  const tableRowData = hasAccess ? tableRow : [];
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing page size
  };

  //   <img
  //   src={DownloadIcon}
  //   alt="download"
  //   style={{ cursor: "pointer" }}
  //   // onClick={() => handleDownload(item)}
  //   onClick={() => {
  //     openInNewTab(
  //       BASE_API_URL +
  //         `/api/pharmacy/stock/invoice/get/&batch_id=${item?.id}`
  //     );
  //   }}
  // />

  return (
    <>
      {/* <Box sx={{ p: 3, fontSize: "24px", fontWeight: "bold" }}>
        List of Batch Details
      </Box> */}

      <Box
        sx={{
          margin: 1,
          backgroundColor: ColorPallet?.bgcolor,
          // marginTop: "10px",
          // padding: "10px",
          borderRadius: "5px",
          "@media screen and (max-width: 1024px)": {
            padding: "7px",
            margin: 2,
          },
          "@media screen and (max-width: 768px)": {
            padding: "5px",
            margin: 1,
          },
          "@media screen and (max-width: 480px)": {
            padding: "3px",
            margin: 1,
          },
          minHeight: {
            xs: "auto",
            sm: "80vh",
          },
          // my: "1.2%",
        }}
      >
        {/** Search bar her start */}
        <Box
          sx={{
            mt: 2,
            px: 1,
            display: "flex",
            flexDirection: { sm: "column", md: "row" },
            gap: 2,
            justifyContent: "space-between",
          }}
        >
          <TextField
            inputRef={searchInputRef} // Assign ref to maintain focus
            variant="outlined"
            size="small"
            placeholder="Search..."
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: "350px" }}
          />

          {Account_Type === 0 && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexDirection: { xs: "column", sm: "row", md: "row" },
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={addedByChecked}
                      onChange={handleAddedByChange}
                    />
                  }
                  label="Added By"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={updatedByChecked}
                      onChange={handleUpdatedByChange}
                    />
                  }
                  label="Updated By"
                />
                <TextField
                  type="date"
                  label="Date"
                  value={selectedDate}
                  size={"small"}
                  onChange={(e) => {
                    setSelectedDate(e?.target?.value);
                    setPage(0);
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ width: "227px" }} // Adjust the width here as needed
                />
                <Box sx={{ width: { xs: 150, sm: 200 } }}>
                  <FormControl fullWidth>
                    <Select
                      value={selectedId}
                      onChange={handleChange}
                      displayEmpty
                      defaultValue={selectedId}
                      sx={{
                        height: "40px",
                        marginLeft: { xs: "0px", sm: "10px", md: "10px" },
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 200,
                            overflowY: "auto",
                          },
                        },
                      }}
                    >
                      <MenuItem value={undefined}>All</MenuItem>
                      <MenuItem value={"admin"}>Admin</MenuItem>
                      {options.map((option: any) => (
                        <MenuItem key={option.id} value={option.id}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
        {/** Search bar her end */}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              fontSize: "14px",
              fontWeight: 400,
              flexDirection: "column",
              "@media screen and (max-width: 480px)": {
                fontSize: "12px",
              },
            }}
          >
            {/* {expiredCount > 0 && ( */}
            {expireMedCount > 0 && (
              <Box
                sx={{
                  py: "2px",
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                  mt: "5px",
                }}
              >
                {!isFromExpireMedBatch && (
                  <Box sx={{ paddingTop: "2px", paddingLeft: "10px" }}>
                    <WarningIcon />
                  </Box>
                )}
                {!isFromExpireMedBatch && (
                  <Box>
                    Some batch medicines are{" "}
                    <span style={{ color: "red" }}>expired.</span>
                  </Box>
                )}
              </Box>
            )}

            {/* {nearExpiryCount > 0 && ( */}
            {nearExpireMedCount > 0 && (
              <Box
                sx={{
                  py: "2px",
                  display: "flex",
                  flexDirection: "row",
                  gap: 1,
                }}
              >
                {!isFromExpireMedBatch && (
                  <Box sx={{ paddingTop: "2px", paddingLeft: "10px" }}>
                    <WarningIconYellow />
                  </Box>
                )}
                {!isFromExpireMedBatch && (
                  <Box>
                    Some batch medicines are{" "}
                    <span style={{ color: "red" }}>near expiration.</span>
                  </Box>
                )}
              </Box>
            )}
          </Box>

          {!isFromExpireMedBatch && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: { xs: 0, sm: 0, md: 2 },
                "@media screen and (max-width: 768px)": {
                  flexDirection: "column",
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Checkbox
                  size="small"
                  checked={showExpired}
                  onChange={(e) => {
                    setShowExpired(e?.target?.checked);
                    setPage(0);
                  }}
                />
                <Box>
                  Expired Medicines (
                  {expireMedCount && tableRowData.length ? expireMedCount : 0})
                </Box>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Checkbox
                  size="small"
                  checked={showNearExpiry}
                  onChange={(e) => {
                    setShowNearExpiry(e?.target?.checked);
                    setPage(0);
                  }}
                />
                <Box>
                  Near Expiry Medicines (
                  {nearExpireMedCount && tableRowData.length
                    ? nearExpireMedCount
                    : 0}
                  )
                </Box>
              </Box>

              <Box
                sx={{
                  mt: -1,
                  p: 1,
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                <EditBatchToolbar
                  batch={batchData}
                  isEdit={false}
                  setExpireMedCount={setExpireMedCount}
                  setNearExpireMedCount={setNearExpireMedCount}
                />
              </Box>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            px: 1,
            paddingBottom: 1,
            width: "100%",
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "75vh", // Set height to full viewport height
              }}
            >
              <LoadingSpinner />
            </Box>
          ) : (
            <AllTable
              headerData={BatchHeader}
              rowData={tableRowData}
              page={page}
              rowsPerPage={rowsPerPage}
              totalCount={totalCount}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              hasAccess={hasAccess}
            />
          )}
        </Box>
      </Box>

      {/* <HideBatchModel /> */}
      <HideBatchModel
        open={openModal}
        onClose={handleCloseModal}
        setOpenSnackbar={setOpenSnackbar}
        selectedBatchId={selectedBatchId}
        setApiError={setApiError}
        apiError={apiError}
        setTotalCount={setTotalCount}
        setExpireMedCount={setExpireMedCount}
        setNearExpireMedCount={setNearExpireMedCount}
        page={page}
        rowsPerPage={rowsPerPage}
      />
      <AppSnackbar
        message="Medicine hidden successfully!"
        open={openSnackbar}
        handleClose={() => setOpenSnackbar(false)}
      />
    </>
  );
};
export default Batch;
